import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import {
  h,
  computed,
  toRefs,
  useCssModule,
  useSlots,
  ref,
} from 'vue';

import type { PropType } from 'vue';
import { Nullable, Undefinable } from '@/features/shared/types';


export default /*#__PURE__*/_defineComponent({
  __name: 'UiProgressCircular',
  props: {
  button: {
    type: Boolean as PropType<boolean>,
    default: false,
  },

  indeterminate: {
    type: Boolean as PropType<boolean>,
    default: false,
  },

  rotate: {
    type: [Number, String] as PropType<number | string>,
    default: 0,
  },

  size: {
    type: [Number, String] as PropType<number | string>,
    default: 32,
  },

  width: {
    type: [Number, String] as PropType<number | string>,
    default: 4,
  },

  value: {
    type: [Number, String] as PropType<number | string>,
    default: 0,
  },
},
  setup(__props) {

const props = __props;

function convertToUnit(
  str: Undefinable<Nullable<string | number>>,
  unit = 'px',
): Undefinable<string> {
  if (str == null || str === '') {
    return undefined;
  }
  if (Number.isNaN(+str)) {
    return String(str);
  }
  return `${Number(str)}${unit}`;
}

const style = useCssModule();
const slots = useSlots();

const radius = ref(20);



const {
  size,
  button,
  indeterminate,
  value,
  width,
  rotate,
} = toRefs(props);

const calculatedSize = computed<number>(() => Number(size.value) + (button.value ? 8 : 0));

const circumference = computed<number>(() => 2 * Math.PI * radius.value);

const classes = computed(() => ({
  [style.isIndeterminate]: indeterminate.value,
}));

const normalizedValue = computed<number>(() => {
  if (value.value < 0) return 0;

  if (value.value > 100) return 100;

  return parseFloat(value.value as string);
});

const strokeDashArray = computed<number>(() => Math.round(circumference.value * 1000) / 1000);

const strokeDashOffset = computed<string>(() => `${((100 - normalizedValue.value) / 100) * circumference.value}px`);

const viewBoxSize = computed<number>(() => radius.value / (1 - Number(width.value) / +size.value));

const strokeWidth = computed<number>(() => (Number(width.value) / +size.value) * viewBoxSize.value * 2);

const styles = computed(() => [
  { height: convertToUnit(calculatedSize.value) },
  { width: convertToUnit(calculatedSize.value) },
]);

const svgStyles = computed(() => [
  { transform: `rotate(${Number(rotate.value)}deg)` },
]);

const genCircle = (name: string, offset: number) => h('circle', {
  class: style[name],
  fill: 'transparent',
  cx: 2 * viewBoxSize.value,
  cy: 2 * viewBoxSize.value,
  r: radius.value,
  'stroke-width': strokeWidth.value,
  'stroke-dasharray': strokeDashArray.value,
  'stroke-dashoffset': offset,
});

const genSvg = () => {
  const children = [
    indeterminate.value || genCircle('underlay', 0),
    genCircle('overlay', parseInt(strokeDashOffset.value, 10)),
  ];

  return h(
    'svg',
    {
      style: svgStyles.value,
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: `${viewBoxSize.value} ${viewBoxSize.value} ${
        2 * viewBoxSize.value
      } ${2 * viewBoxSize.value}`,
    },
    children,
  );
};

const genInfo = () => h(
  'div',
  {
    class: style.info,
  },
  slots.default ? slots.default() : null,
);

const render = () => h(
  'div',
  {
    role: 'progressbar',
    'aria-valuemin': 0,
    'aria-valuemax': 100,
    'aria-valuenow': indeterminate.value
      ? undefined
      : normalizedValue.value,
    class: [style.circular, classes.value],
    style: styles.value,
  },
  [genSvg(), genInfo()],
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(render))
}
}

})